import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import {
  MainTableColumn,
  MainTableColumnId,
  ValueChanges,
  ValuesChanges,
} from '@prlw/core/monitor/types/main-table';
import { OverflowState } from '@prlw/libs/overflow-fader/overflow-fader.class';
import { BuyProductService } from '../../../buy-product/buy-product.service';
import { PinInstrumentService } from '@prlw/core/monitor/pin-instrument.service';
import { Observable, tap } from 'rxjs';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { TableRowComponent } from '../../table-content/table-row.component';

type RowHover = {
  offset: string;
  isVisible: boolean;
  code?: string;
  index?: number;
};

export enum RowState {
  DEFAULT = 'default',
  HOVERED = 'hovered',
  PREVIOUS_HOVERED = 'previous-hovered',
  ACTIVE = 'active',
}

const PLUGS_LENGTH = 15;
const HOVER_MARGIN = 50;
const ROW_HEIGHT = 45;

@Component({
  selector: 'prlw-table-ui',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements AfterViewChecked, OnChanges {
  @ViewChild('scroll') public scroll?: ElementRef;
  @ViewChild('tableContentWrapper', { static: false })
  public readonly tableContentWrapper?: ElementRef;

  public get tableHorizontalScrollPx(): number {
    return this.tableContentWrapper?.nativeElement.scrollLeft ?? 0;
  }

  @ViewChild('tableContent') private readonly tableContent?: ElementRef;

  public get tableContentClientWidthPx(): number {
    return this.tableContent?.nativeElement.clientWidth ?? 0;
  }

  @Input() public data: Instrument[] | null = null;
  @Input() public columns: MainTableColumn<MainTableColumnId>[] | null = null;
  @Input() public valuesChanges: ValuesChanges | null = null;
  @Input() public hasUnselectedBases: boolean | null = null;
  @Input() public noBasesAvailable: boolean | null = null;
  @Input() public noSubProductsSelected: boolean | null = null;
  @Input() public gridSize: string | null = null;
  @Input() public overflows: OverflowState | null = null;
  @Input() public preloaderActive: boolean | null = null;
  @Input() public reqInstrumentsResult: unknown | null = null;
  @Input() public isExpanded: boolean | null = null;

  @Output() public readonly openManageOverlay = new EventEmitter<void>();
  @Output() public readonly openBasisOverlay = new EventEmitter<void>();
  @Output() public readonly removeBasisOutput = new EventEmitter<string>();
  @Output() public readonly removeProductByName = new EventEmitter<string>();
  @Output() public readonly initResizeObserver = new EventEmitter<{
    elemX: HTMLElement;
    elemY: HTMLElement;
  }>();
  @Output() public readonly setScrollHorizontalPosition =
    new EventEmitter<number>();
  @Output() public readonly setScrollVerticalPosition =
    new EventEmitter<number>();
  @Output() public readonly eventToggleActiveMobile = new EventEmitter();
  @Output() private readonly eventOnLoaded = new EventEmitter<void>();

  public readonly loaderPlugsLength = [...Array(PLUGS_LENGTH)];
  public activeBasis: number | null = null;
  private _rowHover!: RowHover;
  private _lastScrollTop: number = 0;

  public pinInstrument$: Observable<Instrument | undefined>;
  public dataWithoutPinned$?: Observable<Instrument[] | null>;

  @ViewChild(TableRowComponent)
  set tableRowComponent(c: TableRowComponent) {
    if (c) {
      this.eventOnLoaded.emit();
    }
  }

  constructor(
    private readonly elementRef: ElementRef,
    private readonly buyProductService: BuyProductService,
    private readonly router: Router,
    private pinInstrumentService: PinInstrumentService,
    private responsiveService: ResponsiveService,
  ) {
    this.setRowHover(null);
    this.pinInstrument$ = pinInstrumentService.pinnedInstrument$.pipe(
      tap((pinnedInstrument) => {
        if (pinnedInstrument) {
          this.activeBasis = null;
        }
      }),
    );
  }

  xsmall$ = this.responsiveService.xsmall$;

  public get mode(): string | null {
    return this.data?.[0]?.mode ?? null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const data = changes.data as SimpleChange | undefined;
    if (data && data.currentValue?.length !== data.previousValue?.length) {
      this.setRowHover(null);
    }
  }

  private overflowFaderInitialized = false;

  public ngAfterViewChecked(): void {
    if (
      !this.overflowFaderInitialized &&
      this.tableContentWrapper &&
      this.scroll
    ) {
      this.initResizeObserver.emit({
        elemX: this.tableContentWrapper.nativeElement,
        elemY: this.scroll.nativeElement,
      });
      this.overflowFaderInitialized = true;
    }
  }

  public getValueChanges(instrumentCode: string): ValueChanges | null {
    return this.valuesChanges?.[instrumentCode] ?? null;
  }

  public openBasis(): void {
    this.openBasisOverlay.emit();
  }

  public onToggleActive(index: number): void {
    if (this.xsmall$.getValue()) {
      if (this.data)
        this.eventToggleActiveMobile.emit({
          instrumentCode: this.data[index].instrumentCode,
        });
      return;
    }
    this.activeBasis = this.activeBasis === index ? null : index;
  }

  public removeBasis(basisCode?: string): void {
    this.removeBasisOutput.emit(basisCode);
  }

  public removeBasisAndUnsetActive(basisCode?: string): void {
    this.removeBasis(basisCode);
    this.activeBasis = null;
  }

  public setHorizontalScroll(event: Event): void {
    const elem = event.target as HTMLElement | null;
    if (elem) {
      this.setScrollHorizontalPosition.emit(elem.scrollLeft);
    }
  }

  public setVerticalScroll(scrollTop: number): void {
    this.setScrollVerticalPosition.emit(scrollTop);
  }

  public processVerticalScroll(event: Event): void {
    const elem = event.target as HTMLElement;
    const scrollTop = elem.scrollTop;
    elem.scrollTop = this._getVerticalScroll(scrollTop);
    this._lastScrollTop = elem.scrollTop;
    this.setVerticalScroll(elem.scrollTop);
  }

  public setRowHover(
    event: MouseEvent | null,
    code?: string,
    index?: number,
  ): void {
    if (this.xsmall$.getValue()) {
      this._rowHover = {
        offset: '0',
        isVisible: false,
      };
      return;
    }

    const elems = document.querySelectorAll(
      'prlw-instrument-remove-button, prlw-instrument-remove-button *',
    );
    const isButton = Array.from(elems).some(
      (el) => el === event?.relatedTarget,
    );

    if (isButton && event) {
      return;
    }

    const elem = event?.target as HTMLElement | undefined;
    const topOffset = Number(elem?.offsetTop);
    const scrollTop = Number(this.scroll?.nativeElement.scrollTop);

    const isTopFit = topOffset >= scrollTop;

    if (code && isTopFit) {
      this._rowHover = {
        offset: `${topOffset + HOVER_MARGIN - scrollTop}px`,
        isVisible: true,
        code,
        index,
      };
    } else {
      this._rowHover = {
        offset: '0',
        isVisible: false,
      };
    }
  }

  public get rowHover(): RowHover {
    return this._rowHover;
  }

  @HostListener('window:keyup', ['$event'])
  private onClose(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.activeBasis = null;
    }
  }

  @HostListener('document:mouseup', ['$event.target'])
  public onClickOutside(targetElement: HTMLElement | undefined): void {
    const closeBtn = targetElement?.classList.contains('btn-close');
    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (
      !clickedInside &&
      this.activeBasis !== null &&
      !closeBtn &&
      !this.buyProductService.isOpen
    ) {
      this.activeBasis = null;
    }
  }

  @HostListener('mouseleave')
  public tableMouseLeave(): void {
    this.setRowHover(null);
  }

  private _getVerticalScroll(newScrollValue: number): number {
    if (newScrollValue < this._lastScrollTop) {
      return Math.floor(newScrollValue / ROW_HEIGHT) * ROW_HEIGHT;
    }
    return Math.ceil(newScrollValue / ROW_HEIGHT) * ROW_HEIGHT;
  }

  public getStateForRow(i: number): RowState {
    if (this.activeBasis === i) {
      return RowState.ACTIVE;
    }
    if (this.rowHover.index === i) {
      return RowState.HOVERED;
    }
    if (this.rowHover.index === i - 1) {
      return RowState.PREVIOUS_HOVERED;
    }
    return RowState.DEFAULT;
  }

  public get rowStateActive() {
    return RowState.ACTIVE;
  }

  public trackByBasisCode(index: number, instrument: Instrument): string {
    return instrument.shipment.code;
  }

  public goToBuyLicense(): void {
    this.router.navigate(['/buy-license']);
  }
}
